import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiClient from '../../../../api/client';
import Header from '../../../../components/Header/Header';
import Footer from '../../../../components/Footer/Footer';
import './Chat.css';
import './Chat.tablet.css';
import '../../../../styles/Modal.css';
import icon_user from '../../../../assets/Main/icon_user.svg';
import back from '../../../../assets/Main/arrow_left_bold.svg';

import ChatSidebar from './ChatSidebar';
import ChatHeader from './ChatHeader';
import ChatMessages from './ChatMessages';
import ChatModals from './ChatModals';
import ProtectedImage from './ProtectedImage';
import {
  getCachedComplaintCategories,
  getCachedCustomerProfile,
  getCachedExecutorInvitations,
  getCachedExecutorProfile,
  invalidateFrontendCache,
} from '../../../../api/frontendCache';

import {
  formatTimeMoscow,
  getDisplayName,
  getOrderTitle,
  getOnlineStatus,
  getIdsFromChat,
  getOrderIdFromChat,
  getExecutorIdFromChat,
} from './chatHelpers';

const Chat = ({ orderId: propOrderId }) => {
  const navigate = useNavigate();

  const [pinnedChats, setPinnedChats] = useState(() => {
    const saved = localStorage.getItem('pinnedChats');
    return saved ? JSON.parse(saved) : [];
  });
  const [archivedChats, setArchivedChats] = useState(new Set());

  const [userRole, setUserRole] = useState(null);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [executorProfile, setExecutorProfile] = useState(null);
  const isExecutor = userRole === 'EXECUTOR';

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1023);
  const [mobileScreen, setMobileScreen] = useState('list');

  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [files, setFiles] = useState([]);

  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);

  const [chatsPage, setChatsPage] = useState(0);
  const [hasMoreChats, setHasMoreChats] = useState(false);
  const [totalChats, setTotalChats] = useState(0);
  const [allChats, setAllChats] = useState([]);
  const [chatSearchValue, setChatSearchValue] = useState('');
  const [searchingChats, setSearchingChats] = useState(false);

  const [invitations, setInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [processingInvitation, setProcessingInvitation] = useState(null);
  const [declinedInvitations, setDeclinedInvitations] = useState(new Set());
  const [showAcceptSuccessModal, setShowAcceptSuccessModal] = useState(false);

  const [supportTicket, setSupportTicket] = useState(null);
  const [supportLoading, setSupportLoading] = useState(true);

  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);

  const [showReview, setShowReview] = useState(false);
  const [modalShowReview, setModalShowReview] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [reviewFiles, setReviewFiles] = useState([]);
  const [currentReviewId, setCurrentReviewId] = useState(null);
  const [loadingReview, setLoadingReview] = useState(false);

  const [scoreQuality, setScoreQuality] = useState(1);
  const [scoreDeadlines, setScoreDeadlines] = useState(1);
  const [scoreBudget, setScoreBudget] = useState(1);
  const [scoreCommunication, setScoreCommunication] = useState(1);
  const [scoreGoodFaith, setScoreGoodFaith] = useState(1);
  const [scoreOverallImpression, setScoreOverallImpression] = useState(1);

  const [suggestedExecutors, setSuggestedExecutors] = useState([]);
  const [suggestedLoading, setSuggestedLoading] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);

  const [scrollThumbPosition, setScrollThumbPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const [sidebarThumbPosition, setSidebarThumbPosition] = useState(0);
  const [showSidebarScrollbar, setShowSidebarScrollbar] = useState(false);

  const [showDeclineConfirmModal, setShowDeclineConfirmModal] = useState(false);
  const [showShareContactsModal, setShowShareContactsModal] = useState(false);
  const [showCompleteOrderModal, setShowCompleteOrderModal] = useState(false);
  const [showCompletionNotification, setShowCompletionNotification] = useState(false);
  const [orderClosed, setOrderClosed] = useState(false);
  const [selectedExecutorForAction, setSelectedExecutorForAction] = useState(null);
  const [showContactsMessage, setShowContactsMessage] = useState(false);
  const [contactsShared, setContactsShared] = useState(false);
  const [showActionButtons, setShowActionButtons] = useState(false);

  const [showCustomerAcceptModal, setShowCustomerAcceptModal] = useState(false);
  const [showCustomerDeclineModal, setShowCustomerDeclineModal] = useState(false);
  const [showShareContactsAfterSelectModal, setShowShareContactsAfterSelectModal] = useState(false);
  const [showExecutorCompletionNotification, setShowExecutorCompletionNotification] = useState(false);
  const [collaborationLoading, setCollaborationLoading] = useState(false);
  const [collaborationState, setCollaborationState] = useState(null);
  const [customerActionButtonsHidden, setCustomerActionButtonsHidden] = useState(false);

  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [complaintCategories, setComplaintCategories] = useState([]);
  const [complaintLoading, setComplaintLoading] = useState(false);
  const [complaintSubmitting, setComplaintSubmitting] = useState(false);

  const messagesRef = useRef(null);
  const headerMenuRef = useRef(null);
  const optionsMenuRef = useRef(null);
  const userInfoRef = useRef(null);
  const optionsButtonRef = useRef(null);
  const chatsRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesRequestRef = useRef(0);
  const supportAutoOpenedRef = useRef(false);

  const [showExecutorContacts, setShowExecutorContacts] = useState(false);
  const [executorContacts, setExecutorContacts] = useState(null);
  const [showCustomerContactsButton, setShowCustomerContactsButton] = useState(false);
  const [sharingContacts, setSharingContacts] = useState(false);


  // Функция завершения заказа
  const handleCompleteOrder = () => {
    setShowCompleteOrderModal(true);
  };

  const confirmCompleteOrder = async () => {
    const orderId = selectedChat?.orderId || selectedChat?.order?.orderId;
    const reason = 'Завершение заказа';

    setCollaborationLoading(true);

    try {
      await apiClient.post(`/customers/me/orders/${orderId}/close`, { reason });

      setSelectedChat((prev) => ({ ...prev, closed: true }));

      // заказчик сразу видит блок отзыва
      setShowReview(true);

      setShowCompleteOrderModal(false);
    } catch (error) {
      console.error('Ошибка закрытия заказа:', error);
      alert(error.response?.data?.message || 'Не удалось закрыть заказ');
    } finally {
      setCollaborationLoading(false);
    }
  };

  const sendCompletionNotificationToExecutor = async () => {
    const orderId = selectedChat?.orderId || selectedChat?.order?.orderId;
    try {
      await apiClient.post(`/orders/${orderId}/complete`);
    } catch (error) {
      console.error('Ошибка уведомления о завершении:', error);
    }
  };

  
  const orderClosedForExecutor = useMemo(() => {
    if (!selectedChat || userRole !== 'EXECUTOR') return false;
    
    const isClosed = selectedChat?.order?.closed || selectedChat?.closed || false;
    const orderStatus = selectedChat?.order?.status;
    const isOrderCompleted = orderStatus === 'CLOSED' || orderStatus === 'COMPLETED';
    
    return isClosed || isOrderCompleted;
  }, [selectedChat, userRole]);


  // Функция для отправки контактов исполнителя
  const shareExecutorContacts = async () => {
    const orderId = selectedChat?.orderId || selectedChat?.order?.orderId;
    
    if (!orderId) {
      alert('Не удалось определить orderId');
      return;
    }

    setSharingContacts(true);

    try {
      const response = await apiClient.post(`/orders/${orderId}/collaboration/share-executor-socials`);
      
      // Обновляем collaboration state
      setCollaborationState(response.data || null);
      await fetchCollaborationState(orderId);
      
      // Показываем сообщение с контактами
      setShowExecutorContacts(true);
      
    } catch (error) {
      console.error('❌ Ошибка отправки контактов исполнителя:', error);
      alert(error.response?.data?.message || 'Не удалось отправить контакты');
    } finally {
      setSharingContacts(false);
    }
  };

  // Функция для отправки контактов заказчика 
  const confirmShareContactsAfterSelect = async () => {
    const orderId = selectedChat?.orderId || selectedChat?.order?.orderId;

    if (!orderId) {
      alert('Не удалось определить orderId');
      return;
    }

    setCollaborationLoading(true);

    try {
      const response = await apiClient.post(
        `/orders/${orderId}/collaboration/share-customer-phone`
      );

      setCollaborationState(response.data || null);
      await fetchCollaborationState(orderId);

      setShowShareContactsAfterSelectModal(false);
      
      // Показываем сообщение с контактами заказчика
      setShowContactsMessage(true);
      setContactsShared(true);
    } catch (error) {
      console.error('Ошибка шаринга телефона заказчика:', error);
      alert(error.response?.data?.message || 'Не удалось поделиться телефоном');
    } finally {
      setCollaborationLoading(false);
    }
  };

  // Функция для обработки нажатия на кнопку "Поделиться контактами" для исполнителя
  const handleExecutorShareContacts = () => {
    setShowShareContactsModal(true);
  };


  // Эффект для отслеживания состояния контактов
  useEffect(() => {
    if (!collaborationState) return;

    // Если исполнитель поделился контактами
    if (collaborationState.executorSocialsShared) {
      setShowExecutorContacts(true);
      // Здесь можно добавить получение контактов исполнителя из профиля
      setExecutorContacts({
        phone: executorProfile?.phone || 'Телефон не указан',
        email: executorProfile?.email || 'Почта не указана'
      });
    }

    // Если заказчик поделился контактами
    if (collaborationState.customerPhoneShared) {
      setShowContactsMessage(true);
      setContactsShared(true);
    }

    // Показываем кнопку заказчику, если исполнитель уже поделился контактами
    if (userRole === 'CUSTOMER' && collaborationState.executorSocialsShared && !collaborationState.customerPhoneShared) {
      setShowCustomerContactsButton(true);
    } else {
      setShowCustomerContactsButton(false);
    }

  }, [collaborationState, userRole, executorProfile]);


  // ЧАТ ПОДДЕРЖКИ
  const location = useLocation()
  // Загрузка тикетов с сервера
  useEffect(() => {
    fetchSupportTicket();
  }, []);

  // Функция загрузки тикета поддержки
  const fetchSupportTicket = async () => {
    setSupportLoading(true);

    try {
      const res = await apiClient.get('/support/tickets/me', {
        params: {
          page: 0,
          size: 20,
        },
      });

      const items = Array.isArray(res.data?.items) ? res.data.items : [];

      const firstTicket = items.find(
        (ticket) =>
          ticket &&
          !ticket.hiddenFromCreator &&
          ticket.category !== 'COMPLAINT_CHAT' &&
          ticket.category !== 'COMPLAINT_USER'
      ) || null;

      if (!firstTicket || typeof firstTicket !== 'object') {
        setSupportTicket(null);
        return null;
      }

      const detailsRes = await apiClient.get(`/support/tickets/${firstTicket.id}`);
      setSupportTicket(detailsRes.data || null);
      return detailsRes.data || null;
    } catch (error) {
      console.error('Ошибка загрузки тикета поддержки:', error);
      setSupportTicket(null);
      return null;
    } finally {
      setSupportLoading(false);
    }
  };

  const SUPPORT_CHAT_ID = 'support-chat-rulla';
  const SUPPORT_WELCOME_MESSAGE = {
    id: 'support-welcome-message',
    senderUserId: 'support',
    text: 'Здравствуйте! Это служба поддержки платформы Rulla.\nОпишите ваш вопрос, и мы ответим в течение 15 минут.',
    createdAt: '2026-04-01T00:00:00.000Z',
    mine: false,
    read: true,
    attachments: [],
    isSystemSupportMessage: true,
  };

  const isSupportChat = (chat) => chat?.type === 'SUPPORT';

  const mapSupportTicketToChat = (ticket) => ({
    id: SUPPORT_CHAT_ID,
    chatId: SUPPORT_CHAT_ID,
    type: 'SUPPORT',
    ticketId: ticket?.id || null,
    title: 'Чат поддержки',
    avatarUrl: null,
    orderTitle:
      ticket?.details?.orderTitle ||
      ticket?.details?.targetOrderTitle ||
      ticket?.details?.targetChatTitle ||
      ticket?.categoryTitle ||
      'Служба поддержки',
    lastMessageAt: ticket?.lastMessageAt || ticket?.createdAt || '2026-04-01T00:00:00.000Z',
    lastMessageText: 'Здравствуйте! Это служба поддержки платформы Rulla.',
    lastMessageRead: true,
    unreadMessagesCount: 0,
    categoryTitle: ticket?.categoryTitle || 'Поддержка',
    status: ticket?.status || 'OPEN',
    canReply: ticket?.canReply ?? true,
  }); 

  // Добавление чата поддержки к остальным 
  const supportChatListItem = useMemo(() => {
    return mapSupportTicketToChat(supportTicket);
  }, [supportTicket]);

  const chatsWithSupport = useMemo(() => {
    const regularChats = Array.isArray(chats) ? chats : [];
    return [supportChatListItem, ...regularChats];
  }, [chats, supportChatListItem]);

  const loadSupportMessages = async (chat, beforeAt = null, beforeId = null) => {
    const ticketId = chat?.ticketId;

    // если тикета ещё нет — просто показываем стартовое сообщение
    if (!ticketId) {
      setMessages([SUPPORT_WELCOME_MESSAGE]);
      setHasMoreMessages(false);
      setNextCursor(null);
      return;
    }

    setMessagesLoading(true);

    try {
      const params = { limit: 50 };

      if (beforeAt && beforeId) {
        params.beforeAt = beforeAt;
        params.beforeId = beforeId;
      }

      const response = await apiClient.get(`/support/tickets/${ticketId}/messages`, { params });

      const supportMessages = (response.data?.items || [])
        .map((msg) => ({
          ...msg,
          mine: true,
          read: true,
          attachments: msg.attachments || [],
        }))
        .reverse();

      const merged = beforeAt && beforeId
        ? [...supportMessages, ...messages.filter((m) => m.id !== SUPPORT_WELCOME_MESSAGE.id)]
        : [SUPPORT_WELCOME_MESSAGE, ...supportMessages];

      setMessages(merged);
      setHasMoreMessages(Boolean(response.data?.hasMore));
      setNextCursor({
        beforeAt: response.data?.nextBeforeAt || null,
        beforeId: response.data?.nextBeforeId || null,
      });
    } catch (error) {
      console.error('Ошибка загрузки сообщений поддержки:', error);
      setMessages([SUPPORT_WELCOME_MESSAGE]);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleSupportSend = async () => {
    if ((!messageInput.trim() && files.length === 0) || !selectedChat) return;

    const textToSend = messageInput.trim();
    const filesToSend = [...files];

    const tempId = `temp-support-${Date.now()}`;
    const tempMsg = {
      id: tempId,
      text: textToSend,
      mine: true,
      read: true,
      createdAt: new Date().toISOString(),
      attachments: filesToSend.map((file) => ({
        id: `temp-${file.name}-${Date.now()}`,
        type: file.type.startsWith('image/') ? 'IMAGE' : 'FILE',
        contentType: file.type,
        url: URL.createObjectURL(file),
        fileName: file.name,
        file,
        isLocal: true,
        status: 'PENDING',
      })),
    };

    setMessages((prev) => [...prev, tempMsg]);
    setMessageInput('');
    setFiles([]);

    try {
      let ticketId = selectedChat.ticketId;
      let createdTicket = null;

      // если тикета ещё нет — создаём его первым сообщением
      if (!ticketId) {
        if (filesToSend.length > 0) {
          const formData = new FormData();

          formData.append(
            'payload',
            new Blob(
              [
                JSON.stringify({
                  category: 'ACCOUNT', // временно захардкожено, пока не сделали выбор категории
                  text: textToSend || '',
                }),
              ],
              { type: 'application/json' }
            )
          );

          filesToSend.forEach((file) => formData.append('files', file));

          const res = await apiClient.post('/support/tickets/with-attachments', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          createdTicket = res.data;
        } else {
          const res = await apiClient.post('/support/tickets', {
            category: 'ACCOUNT',
            text: textToSend,
          });

          createdTicket = res.data;
        }

        ticketId = createdTicket.id;
        setSupportTicket(createdTicket);

        const updatedSupportChat = mapSupportTicketToChat(createdTicket);
        setSelectedChat(updatedSupportChat);

        await loadSupportMessages(updatedSupportChat);
        return;
      }

      // если тикет уже есть — шлём сообщение
      let response;

      if (filesToSend.length > 0) {
        const formData = new FormData();

        formData.append(
          'payload',
          new Blob(
            [
              JSON.stringify({
                text: textToSend || '',
              }),
            ],
            { type: 'application/json' }
          )
        );

        filesToSend.forEach((file) => formData.append('files', file));

        response = await apiClient.post(
          `/support/tickets/${ticketId}/messages/with-attachments`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      } else {
        response = await apiClient.post(`/support/tickets/${ticketId}/messages`, {
          text: textToSend,
        });
      }

      const createdMessage = {
        ...response.data,
        mine: true,
        read: true,
        attachments: response.data?.attachments || [],
      };

      setMessages((prev) => prev.map((msg) => (msg.id === tempId ? createdMessage : msg)));

      const freshTicket = await apiClient.get(`/support/tickets/${ticketId}`);
      setSupportTicket(freshTicket.data || null);
    } catch (error) {
      console.error('Ошибка отправки сообщения в поддержку:', error);
      setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
      setMessageInput(textToSend);
      setFiles(filesToSend);
    }
  };


  // Закрепление чата
  const handlePinChat = (chat) => {
    const chatId = chat.chatId || chat.id;

    setPinnedChats(prev => {
      let newPinned;

      if (prev.includes(chatId)) {
        newPinned = prev.filter(id => id !== chatId);
      } else {
        if (prev.length >= 5) {
          alert('Можно закрепить не более 5 чатов');
          return prev;
        }
        newPinned = [...prev, chatId];
      }

      localStorage.setItem('pinnedChats', JSON.stringify(newPinned));

      setChats(prevChats =>
        prevChats.map(c => {
          const cId = c.chatId || c.id;
          if (cId === chatId) {
            return { ...c, isPinned: !c.isPinned };
          }
          return c;
        })
      );

      setSelectedChat(prevSelected => {
        if (!prevSelected) return prevSelected;
        const selectedId = prevSelected.chatId || prevSelected.id;
        if (selectedId === chatId) {
          return { ...prevSelected, isPinned: !prevSelected.isPinned };
        }
        return prevSelected;
      });

      return newPinned;
    });
  };

  // Эффект для синхронизации pinnedChats с chats
  useEffect(() => {
    if (chats.length > 0) {
      setChats(prevChats => 
        prevChats.map(chat => {
          const chatId = chat.chatId || chat.id;
          return {
            ...chat,
            isPinned: pinnedChats.includes(chatId)
          };
        })
      );
    }
  }, [pinnedChats]);


  // ===== ЖАЛОБЫ =====
  // Загрузка категорий жалоб
  useEffect(() => {
    if (showComplaintModal && complaintCategories.length === 0) {
      fetchComplaintCategories();
    }
  }, [showComplaintModal]);

  const fetchComplaintCategories = async () => {
    setComplaintLoading(true);

    try {
      const categories = await getCachedComplaintCategories();
      setComplaintCategories(Array.isArray(categories) ? categories : []);
    } catch (error) {
      console.error('Ошибка загрузки категорий жалоб:', error);
      setComplaintCategories([]);
    } finally {
      setComplaintLoading(false);
    }
  };

  const handleSubmitComplaint = async ({ categoryCode, text, files }) => {
    if (!selectedChat) return;

    setComplaintSubmitting(true);

    try {
      const payload = {
        category: categoryCode,
        text: text?.trim() || '',
        targetChatId: selectedChat?.chatId || selectedChat?.id || null,
      };

      const executorId = getExecutorIdFromChat(selectedChat);
      if (executorId) {
        payload.targetExecutorId = executorId;
      }

      if (selectedChat?.customer?.id) {
        payload.targetCustomerId = selectedChat.customer.id;
      }

      if (selectedChat?.customerUserId) {
        payload.targetUserId = selectedChat.customerUserId;
      }

      const response = await apiClient.post('/support/tickets', payload);
      const createdTicket = response.data;

      if (files?.length) {
        await uploadComplaintFiles(createdTicket.id, files);
      }

      alert('Жалоба отправлена');
      setShowComplaintModal(false);
    } catch (error) {
      console.error('❌ Ошибка отправки жалобы:', error);
      alert(error.response?.data?.message || 'Не удалось отправить жалобу');
    } finally {
      setComplaintSubmitting(false);
    }
  };

  const uploadComplaintFiles = async (ticketId, files) => {
    if (!ticketId || !files?.length) return;

    const results = await Promise.allSettled(
      files.map((file) => {
        const formData = new FormData();
        formData.append('files', file);

        return apiClient.post(`/support/tickets/${ticketId}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      })
    );

    const failed = results.filter((item) => item.status === 'rejected');
    if (failed.length > 0) {
      console.warn(`Не удалось загрузить ${failed.length} файл(ов)`);
    }
  };


  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1023;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileScreen('chat');
      } else if (!selectedChat) {
        setMobileScreen('list');
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [selectedChat]);


  const goBackToChatList = () => {
    setMobileScreen('list');
    setShowHeaderMenu(false);
    setShowOptionsMenu(false);
  };

  const checkScrollbarNeeded = () => {
    if (messagesRef.current && !isMobile) {
      const { scrollHeight, clientHeight } = messagesRef.current;
      setShowScrollbar(scrollHeight > clientHeight);
    } else {
      setShowScrollbar(false);
    }
  };

  const checkSidebarScrollbarNeeded = () => {
    if (!chatsRef.current) return;
    const { scrollHeight, clientHeight } = chatsRef.current;
    setShowSidebarScrollbar(scrollHeight > clientHeight);
  };

  const selectedChatExecutorCard = selectedChat
    ? {
        id: getExecutorIdFromChat(selectedChat),
        name: getDisplayName(selectedChat, userRole),
        avatar: selectedChat.selectedExecutorAvatarUrl || selectedChat.executorAvatarUrl || null,
        rating: selectedChat.executorRating || selectedChat.selectedExecutorRating || 4.9,
        reviewsCount:
          selectedChat.executorReviewsCount || selectedChat.selectedExecutorReviewsCount || 39,
        specialization:
          selectedChat.executorSpecializationsText ||
          selectedChat.selectedExecutorSpecializationsText ||
          'Сантехник, Электрик, Маляр',
      }
    : null;

const renderAttachments = (attachments) => {
  if (!attachments || attachments.length === 0) return null;

  return (
    <div className="message-attachments">
      {attachments.map((attachment) => {
        const isPending = attachment.status === 'PENDING';

        if (attachment.type === 'IMAGE') {
          if (isPending && attachment.file) {
            const localUrl = URL.createObjectURL(attachment.file);

            return (
              <div key={attachment.id} className="message-attachment-image-wrapper">
                <img
                  src={localUrl}
                  alt="attachment"
                  className="message-attachment-image"
                  onLoad={(e) => URL.revokeObjectURL(e.target.src)}
                />
                <div className="message-attachment-pending">Загрузка...</div>
              </div>
            );
          }

          if (isPending && !attachment.file) {
            return (
              <div key={attachment.id} className="message-attachment-image-wrapper">
                <div className="message-attachment-pending">
                  Изображение проходит обработку...
                </div>
              </div>
            );
          }

          return (
            <div key={attachment.id} className="message-attachment-image-wrapper">
              <ProtectedImage
                url={attachment.url}
                alt="attachment"
                className="message-attachment-image"
              />
            </div>
          );
        }

        if (isPending) {
          return (
            <div key={attachment.id} className="message-attachment-file">
              <div className="message-attachment-file-icon">📄</div>
              <span className="message-attachment-file-text">
                Файл проходит обработку...
              </span>
            </div>
          );
        }

        // return (
        //   <div key={attachment.id} className="message-attachment-file">
        //     <div className="message-attachment-file-icon">📄</div>
        //     <ProtectedFileLink attachment={attachment} />
        //   </div>
        // );
      })}
    </div>
  );
};

  const fetchCollaborationState = async (orderId) => {
    if (!orderId) return;

    try {
      const response = await apiClient.get(`/orders/${orderId}/collaboration`);
      setCollaborationState(response.data || null);
    } catch (error) {
      console.error('Ошибка загрузки collaboration state:', error);
      setCollaborationState(null);
    }
  };

  useEffect(() => {
    if (!collaborationState) {
      setShowContactsMessage(false);
      setContactsShared(false);
      setShowExecutorContacts(false);
      setShowCustomerContactsButton(false);
      return;
    }

    setShowContactsMessage(Boolean(collaborationState.customerPhoneShared));
    setContactsShared(Boolean(collaborationState.customerPhoneShared));
    setShowExecutorContacts(Boolean(collaborationState.executorSocialsShared));

    if (
      userRole === 'CUSTOMER' &&
      collaborationState.executorSocialsShared &&
      !collaborationState.customerPhoneShared
    ) {
      setShowCustomerContactsButton(true);
    } else {
      setShowCustomerContactsButton(false);
    }
  }, [collaborationState, userRole]);

  // Показ модалки исполнителю после того, как заказ уже закрыт
  useEffect(() => {
    if (!selectedChat || userRole !== 'EXECUTOR') return;

    const orderId = selectedChat?.orderId || selectedChat?.order?.orderId;
    if (!orderId) return;

    const modalShownKey = `executor-completion-modal-shown-${orderId}`;

    if (selectedChat.closed && !sessionStorage.getItem(modalShownKey)) {
      setShowExecutorCompletionNotification(true);
      sessionStorage.setItem(modalShownKey, 'true');
    }

    if (selectedChat.closed) {
      setShowReview(true);
    }
  }, [selectedChat, userRole]);

  // Получение роли пользователя
  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        if (userRole === 'EXECUTOR') {
          const profile = await getCachedExecutorProfile();
          setExecutorProfile(profile);
          setCurrentUserId(profile.userId);
        } else if (userRole === 'CUSTOMER') {
          const profile = await getCachedCustomerProfile();
          setCustomerProfile(profile);
          setCurrentUserId(profile.userId);
        }
      } catch (error) {
        console.error('Ошибка загрузки профиля:', error);
      }
    };

    if (userRole) {
      fetchProfiles();
    }
  }, [userRole]);

  const handleAccept = () => {
    setSelectedExecutorForAction(selectedChat);
    setShowShareContactsModal(true);
  };

  const handleDecline = () => {
    setSelectedExecutorForAction(selectedChat);
    setShowDeclineConfirmModal(true);
  };

  const confirmDecline = async () => {
    try {
      if (selectedChat?.invitationId) {
        await declineInvitation(selectedExecutorForAction.id);
      }
    } catch (error) {
      console.error('Ошибка отклонения приглашения:', error);
    }

    setShowActionButtons(false);
    setShowDeclineConfirmModal(false);
    setSelectedExecutorForAction(null);
  };

  const confirmShareContacts = async () => {
    try {
      if (selectedChat?.invitationId) {
        await acceptInvitation(selectedExecutorForAction.id);
      } else {
        // Если это обмен контактами после выбора исполнителя
        await shareExecutorContacts();
      }
    } catch (error) {
      console.error('Ошибка:', error);
    }

    setShowShareContactsModal(false);
    setShowContactsMessage(true);
    setContactsShared(true);
    setShowActionButtons(false);
  };

  const handleShareContacts = () => {
    setShowShareContactsModal(true);
  };

  const handleDeclineContacts = () => {
    setShowActionButtons(false);
  };

  const updateScrollThumb = () => {
    if (!messagesRef.current || isMobile) return;

    const { scrollTop, scrollHeight, clientHeight } = messagesRef.current;
    const maxScroll = scrollHeight - clientHeight;
    const ratio = maxScroll > 0 ? scrollTop / maxScroll : 0;
    const track = document.querySelector('.scroll-track');

    if (!track) return;

    const trackHeight = track.clientHeight;
    const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * trackHeight);
    const thumb = document.querySelector('.scroll-thumb');

    if (thumb) {
      thumb.style.height = `${thumbHeight}px`;
    }

    setScrollThumbPosition(ratio * (trackHeight - thumbHeight));
  };

  const handleTrackClick = (e) => {
    if (!messagesRef.current || isMobile) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickY = e.clientY - trackRect.top;
    const trackHeight = trackRect.height;
    const clickRatio = Math.max(0, Math.min(1, clickY / trackHeight));
    const { scrollHeight, clientHeight } = messagesRef.current;
    const maxScroll = scrollHeight - clientHeight;

    messagesRef.current.scrollTop = clickRatio * maxScroll;
  };

  const handleThumbMouseDown = (e) => {
    if (isMobile) return;

    e.preventDefault();
    setIsDragging(true);
    const startY = e.clientY;
    const startScrollTop = messagesRef.current?.scrollTop || 0;
    const { scrollHeight = 0, clientHeight = 0 } = messagesRef.current || {};
    const maxScroll = scrollHeight - clientHeight;

    const handleMouseMove = (moveEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const track = document.querySelector('.scroll-track');

      if (!track || !messagesRef.current) return;

      const trackHeight = track.clientHeight;
      const thumbHeight = document.querySelector('.scroll-thumb')?.clientHeight || 30;
      const maxThumbTop = trackHeight - thumbHeight;
      const deltaRatio = deltaY / maxThumbTop;

      messagesRef.current.scrollTop = Math.max(
        0,
        Math.min(maxScroll, startScrollTop + deltaRatio * maxScroll)
      );
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleScroll = () => {
    if (!messagesRef.current) return;

    const { scrollTop } = messagesRef.current;
    if (scrollTop === 0 && hasMoreMessages && !messagesLoading) {
      loadMoreMessages();
    }
  };

  const handleScrollWithThumb = () => {
    handleScroll();
    updateScrollThumb();
  };

  const updateSidebarScrollThumb = () => {
    if (!chatsRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = chatsRef.current;
    const maxScroll = scrollHeight - clientHeight;
    const ratio = maxScroll > 0 ? scrollTop / maxScroll : 0;

    const track = document.querySelector('.sidebar-scroll-track');
    if (!track) return;

    const trackHeight = track.clientHeight;
    const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * trackHeight);

    const thumb = document.querySelector('.sidebar-scroll-thumb');

    if (thumb) {
      thumb.style.height = `${thumbHeight}px`;
    }

    setSidebarThumbPosition(ratio * (trackHeight - thumbHeight));
  };

  const handleSidebarScroll = () => {
    updateSidebarScrollThumb();
  };

  useEffect(() => {
    const el = chatsRef.current;
    if (!el) return;

    el.addEventListener('scroll', handleSidebarScroll);

    const timeout = setTimeout(() => {
      updateSidebarScrollThumb();
      checkSidebarScrollbarNeeded();
    }, 100);

    const observer = new ResizeObserver(() => {
      updateSidebarScrollThumb();
      checkSidebarScrollbarNeeded();
    });

    observer.observe(el);

    return () => {
      clearTimeout(timeout);
      el.removeEventListener('scroll', handleSidebarScroll);
      observer.disconnect();
    };
  }, [chats]);

  useEffect(() => {
    const role = localStorage.getItem('userRole');
    setUserRole(role);
  }, []);

  useEffect(() => {
    const fetchChats = async () => {
      if (!userRole) return;

      setLoading(true);

      try {
        if (userRole === 'EXECUTOR') {
          const response = await apiClient.get('/executors/me/my-chats', {
            params: { page: 0, size: 20, includeArchived: false },
          });

          const chatData = response.data;
          setChats(chatData.items || []);
          setAllChats(chatData.items || []);  
          setHasMoreChats(!chatData.last);
          setTotalChats(chatData.totalElements || 0);
          setChatsPage(0);
          fetchInvitations();
        } else {
          if (!propOrderId) {
            setChats([]);
            return;
          }

          const response = await apiClient.get(`/orders/${propOrderId}/chats`);
          setChats(response.data?.items || []);
          setAllChats(response.data?.items || []);
          setTotalChats(response.data?.total || 0);
          setHasMoreChats(false);
          setChatsPage(0);
        }
      } catch (error) {
        console.error('Ошибка загрузки чатов:', error);
        setChats([]);
      } finally {
        setLoading(false);
      }
    };

    fetchChats();
  }, [propOrderId, userRole]);

  // Поиск по чатам
  const handleChatSearch = async () => {
    const query = chatSearchValue.trim();

    if (!query) {
      setChats(allChats);
      return;
    }

    setSearchingChats(true);
    setLoading(true);

    try {
      if (userRole === 'CUSTOMER') {
        if (!propOrderId) {
          setChats([]);
          return;
        }

        const response = await apiClient.get(`/orders/${propOrderId}/chats`, {
          params: {
            executorName: query,
          },
        });

        setChats(response.data?.items || []);
        return;
      }

      if (userRole === 'EXECUTOR') {
        const response = await apiClient.get('/executors/me/my-chats', {
          params: {
            page: 0,
            size: 20,
            includeArchived: false,
            query,
          },
        });

        const chatData = response.data;
        setChats(chatData.items || []);
        setHasMoreChats(!chatData.last);
        setTotalChats(chatData.totalElements || 0);
        setChatsPage(0);
        return;
      }
    } catch (error) {
      console.error('Ошибка поиска чатов:', error);
      setChats([]);
    } finally {
      setSearchingChats(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!chatSearchValue.trim()) {
      setChats(allChats);
    }
  }, [chatSearchValue, allChats]);

  const fetchInvitations = async () => {
    if (userRole !== 'EXECUTOR') return;

    setInvitationsLoading(true);

    try {
      const data = await getCachedExecutorInvitations();
      setInvitations(data);
    } catch (error) {
      console.error('Ошибка загрузки приглашений:', error);
    } finally {
      setInvitationsLoading(false);
    }
  };

  const loadMessages = async (chat, beforeAt = null, beforeId = null) => {
    const requestId = ++messagesRequestRef.current;
    setMessagesLoading(true);

    try {
      const params = { limit: 30 };
      if (beforeAt && beforeId) {
        params.beforeAt = beforeAt;
        params.beforeId = beforeId;
      }

      const chatIdToUse = chat.chatId || chat.id;    
      const response = await apiClient.get(`/orders/chats/${chatIdToUse}/messages`, { params });

      if (requestId !== messagesRequestRef.current) return;
      
      const newMessages = response.data.items.map(msg => {      
        return {
          ...msg,
          attachments: msg.attachments || []
        };
      }).reverse(); // Переворачиваем для правильного порядка отображения

      setMessages((prev) => {
        const updatedMessages = beforeAt && beforeId 
          ? [...newMessages, ...prev] 
          : newMessages;
        
        return updatedMessages;
      });
      
      setHasMoreMessages(response.data.hasMore);
      setNextCursor(response.data.nextCursor);
    } catch (error) {
      if (requestId !== messagesRequestRef.current) return;
      console.error('❌ Ошибка загрузки сообщений:', error);
    } finally {
      if (requestId === messagesRequestRef.current) {
        setMessagesLoading(false);
      }
    }
  };

  const loadMoreChats = async () => {
    if (!hasMoreChats || loading || userRole !== 'EXECUTOR' || chatSearchValue.trim()) return;

    setLoading(true);

    try {
      const nextPage = chatsPage + 1;
      const response = await apiClient.get('/executors/me/my-chats', {
        params: { page: nextPage, size: 20, includeArchived: false },
      });
      const chatData = response.data;
      setChats((prev) => [...prev, ...(chatData.items || [])]);
      setAllChats((prev) => [...prev, ...(chatData.items || [])]);
      setChatsPage(nextPage);
      setHasMoreChats(!chatData.last);
    } catch (error) {
      console.error('Ошибка загрузки следующих чатов:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchOrderData = async () => {
      if (!propOrderId || userRole !== 'CUSTOMER') return;

      setOrderLoading(true);

      try {
        const response = await apiClient.get(`/customers/me/orders/${propOrderId}`);
        setOrderData(response.data || null);
      } catch (error) {
        console.error('Ошибка загрузки данных заказа:', error);
      } finally {
        setOrderLoading(false);
      }
    };

    fetchOrderData();
  }, [propOrderId, userRole]);

  const loadSuggestedExecutors = async () => {
    if (userRole !== 'CUSTOMER' || orderLoading || !orderData?.order) return;

    setSuggestedLoading(true);

    try {
      const params = { page: 0, size: 12 };
      const services = orderData.order.services || [];

      if (services.length > 0 && services[0].categoryId) {
        params.categoryIds = [services[0].categoryId];
      } else if (services.length > 0 && services[0].serviceId) {
        params.serviceIds = [services[0].serviceId];
      }

      if (orderData.order.location?.cityId) {
        params.cityIds = [orderData.order.location.cityId];
      }

      let res = await apiClient.get('/public/executors', { params });
      let items = res.data?.items || [];

      if (items.length < 5 && orderData.order.location?.cityId) {
        const { cityIds, ...rest } = params;
        res = await apiClient.get('/public/executors', { params: rest });
        items = [
          ...new Map([...items, ...(res.data?.items || [])].map((item) => [item.id, item])).values(),
        ];
      }

      if (items.length < 5) {
        res = await apiClient.get('/public/executors', { params: { page: 0, size: 8 } });
        items = [
          ...new Map([...items, ...(res.data?.items || [])].map((item) => [item.id, item])).values(),
        ];
      }

      setSuggestedExecutors(items.slice(0, 5));
    } catch (err) {
      console.error('Ошибка загрузки исполнителей:', err);
      setSuggestedExecutors([]);
    } finally {
      setSuggestedLoading(false);
    }
  };

  useEffect(() => {
    if (userRole === 'CUSTOMER' && orderData) {
      loadSuggestedExecutors();
    }
  }, [orderData, userRole]);

  const selectChat = async (chat) => {
    // Правильное получение closed из объекта order
    const closedStatus = chat?.order?.closed || chat?.closed || false;
    
    console.log('📋 [selectChat] Выбран чат:', {
      chatId: chat.chatId || chat.id,
      orderId: chat.orderId || chat.order?.orderId,
      closed: closedStatus,
      orderClosed: chat?.order?.closed,
      topLevelClosed: chat?.closed,
      type: chat.type,
      orderStatus: chat?.order?.status,
      hasOrderObject: !!chat.order
    });
    
    setSelectedChat(chat);
    setMessages([]);
    setHasMoreMessages(false);
    setNextCursor(null);
    setShowHeaderMenu(false);
    setShowOptionsMenu(false);
    setShowActionButtons(true);
    setShowContactsMessage(false);
    setContactsShared(false);
    setCustomerActionButtonsHidden(false);

    if (isSupportChat(chat)) {
      await loadSupportMessages(chat);
    } else {
      loadMessages(chat);
    }

    if (!isSupportChat(chat)) {
      const orderId = chat?.orderId || chat?.order?.orderId;
      if (orderId) {
        fetchCollaborationState(orderId);
      }
    }

    if (isMobile) {
      setMobileScreen('chat');
    }
  };

  useEffect(() => {
    if (
      !location.state?.openSupport ||
      supportLoading ||
      supportAutoOpenedRef.current
    ) {
      return;
    }

    supportAutoOpenedRef.current = true;
    selectChat(supportChatListItem);
    navigate('/chat', { replace: true, state: null });
  }, [location.state, navigate, supportChatListItem, supportLoading]);

  const loadMoreMessages = () => {
    if (!hasMoreMessages || !nextCursor || messagesLoading || !selectedChat) return;

    if (isSupportChat(selectedChat)) {
      if (!nextCursor.beforeAt || !nextCursor.beforeId) return;

      loadSupportMessages(selectedChat, nextCursor.beforeAt, nextCursor.beforeId);
      return;
    }

    loadMessages(selectedChat, nextCursor.beforeAt, nextCursor.beforeId);
  };

  const handleChatsScroll = (e) => {
    if (userRole !== 'EXECUTOR') return;

    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMoreChats && !loading) {
      loadMoreChats();
    }
  };

  const handleSend = async () => {
    if ((!messageInput.trim() && files.length === 0) || !selectedChat) return;

    if (isSupportChat(selectedChat)) {
      await handleSupportSend();
      return;
    }

    const chatIdToUse = selectedChat.chatId || selectedChat.id;
    if (!chatIdToUse) return;

    const formData = new FormData();
    if (messageInput.trim()) formData.append('text', messageInput);
    files.forEach((file) => formData.append('files', file));

    const tempMsg = {
      id: `temp-${Date.now()}`,
      text: messageInput,
      mine: true,
      read: false,
      createdAt: new Date().toISOString(),
      attachments: files.map((file) => ({
        id: `temp-${file.name}-${Date.now()}`,
        documentId: null,
        type: file.type.startsWith('image/') ? 'IMAGE' : 'DOCUMENT',
        status: 'PENDING',
        file,
      })),
    };

    setMessages((prev) => [...prev, tempMsg]);

    const textToSend = messageInput;
    const filesToSend = [...files];

    setMessageInput('');
    setFiles([]);

    try {
      const response = await apiClient.post(`/orders/chats/${chatIdToUse}/messages`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // обновляем список чатов
      setChats((prev) =>
        prev.map((chat) => {
          const chatIdentifier = chat.chatId || chat.id;
          if (chatIdentifier === chatIdToUse) {
            return {
              ...chat,
              lastMessageText: response.data.text || (filesToSend.length > 0 ? 'Вложение' : ''),
              lastMessageAt: response.data.createdAt,
              lastMessageRead: false,
              unreadMessagesCount: 0,
              lastMessageIsMine: true,
            };
          }
          return chat;
        })
      );

      // вместо использования attachments из POST — перечитываем сообщения из GET
      await loadMessages(selectedChat);
    } catch (error) {
      console.error('Ошибка отправки сообщения:', error);
      setMessages((prev) => prev.filter((msg) => msg.id !== tempMsg.id));
      setMessageInput(textToSend);
      setFiles(filesToSend);
    }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files).slice(0, 3 - files.length);
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const archiveChat = async (chat) => {
    if (!chat) return;

    const chatId = chat.chatId || chat.id;
    const orderId = chat.orderId || chat.order?.orderId;

    try {
      await apiClient.post(`/executors/me/my-chats/${chatId}/archive`);
      
      // Удаляем чат из списка chats
      setChats(prevChats => prevChats.filter(c => {
        const currentChatId = c.chatId || c.id;
        return currentChatId !== chatId;
      }));
      
      // Если архивируемый чат был выбран - сбрасываем выбор
      setSelectedChat(prevSelected => {
        if (!prevSelected) return prevSelected;
        const selectedId = prevSelected.chatId || prevSelected.id;
        return selectedId === chatId ? null : prevSelected;
      });

      // Показываем уведомление об успехе (опционально)
      // alert('Чат успешно архивирован');
      
    } catch (error) {
      console.error('Ошибка архивации чата:', error);
      alert('Не удалось архивировать чат');
    }
  };

  const acceptInvitation = async (invitationId) => {
    setProcessingInvitation(invitationId);

    try {
      await apiClient.post(`/executors/me/invitations/${invitationId}/accept`);
      invalidateFrontendCache('executor-invitations-sent');
      setInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));
    } catch (error) {
      console.error('Ошибка при принятии приглашения:', error);
    } finally {
      setProcessingInvitation(null);
    }
  };

  const declineInvitation = async (invitationId) => {
    setProcessingInvitation(invitationId);

    try {
      await apiClient.post(`/executors/me/invitations/${invitationId}/decline`);
      invalidateFrontendCache('executor-invitations-sent');
      setDeclinedInvitations((prev) => new Set(prev).add(invitationId));
    } catch (error) {
      console.error('Ошибка при отклонении приглашения:', error);
    } finally {
      setProcessingInvitation(null);
    }
  };

  const handleCustomerAcceptClick = () => {
    setShowCustomerAcceptModal(true);
  };

  const handleCustomerDeclineClick = () => {
    setShowCustomerDeclineModal(true);
  };

    const confirmCustomerAccept = async () => {
    const orderId = selectedChat?.orderId || selectedChat?.order?.orderId;
    const executorId = getExecutorIdFromChat(selectedChat);

    if (!orderId || !executorId) {
        alert('Не удалось определить orderId или executorId');
        return;
    }

    setCollaborationLoading(true);

    try {
        const response = await apiClient.post(
        `/orders/${orderId}/collaboration/select-executor`,
        { executorId }
        );

        setCollaborationState(response.data || null);
        await fetchCollaborationState(orderId);

        setShowCustomerAcceptModal(false);
        setCustomerActionButtonsHidden(true);
        setShowShareContactsAfterSelectModal(true);
    } catch (error) {
        console.error('Ошибка выбора исполнителя:', error);
        alert(error.response?.data?.message || 'Не удалось выбрать исполнителя');
    } finally {
        setCollaborationLoading(false);
    }
    };

  const confirmCustomerDecline = async () => {
    const orderId = selectedChat?.orderId || selectedChat?.order?.orderId;

    if (!orderId) {
      alert('Не удалось определить orderId');
      return;
    }

    setCollaborationLoading(true);

    try {
      const response = await apiClient.post(`/orders/${orderId}/collaboration/unassign-executor`);
      setCollaborationState(response.data || null);
      setShowCustomerDeclineModal(false);
      setCustomerActionButtonsHidden(true);
    } catch (error) {
      console.error('Ошибка снятия исполнителя:', error);
      alert(error.response?.data?.message || 'Не удалось отклонить исполнителя');
    } finally {
      setCollaborationLoading(false);
    }
  };

   

  const cancelShareContactsAfterSelect = () => {
    setShowShareContactsAfterSelectModal(false);
  };

  const goToExecutorProfile = (executorId) => {
    navigate(`/executor_profile/${executorId}`);
  };

  const goToOrderDetails = (orderId) => {
    navigate(`/order/${orderId}`);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showHeaderMenu &&
        headerMenuRef.current &&
        !headerMenuRef.current.contains(event.target) &&
        userInfoRef.current &&
        !userInfoRef.current.contains(event.target)
      ) {
        setShowHeaderMenu(false);
      }

      if (
        showOptionsMenu &&
        optionsMenuRef.current &&
        !optionsMenuRef.current.contains(event.target) &&
        optionsButtonRef.current &&
        !optionsButtonRef.current.contains(event.target)
      ) {
        setShowOptionsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showHeaderMenu, showOptionsMenu]);

  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;

    el.addEventListener('scroll', handleScrollWithThumb);

    const timeout = setTimeout(() => {
      updateScrollThumb();
      checkScrollbarNeeded();
    }, 100);

    const observer = new ResizeObserver(() => {
      updateScrollThumb();
      checkScrollbarNeeded();
    });
    observer.observe(el);

    return () => {
      clearTimeout(timeout);
      el.removeEventListener('scroll', handleScrollWithThumb);
      observer.disconnect();
    };
  }, [messages, selectedChat, isMobile]);

  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;

    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [hasMoreMessages, messagesLoading, selectedChat]);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  const uploadReviewAttachments = async (reviewId) => {
    if (!reviewId || !reviewFiles?.length) return;

    for (let i = 0; i < reviewFiles.length; i++) {
      const item = reviewFiles[i];
      const actualFile = item instanceof File ? item : item?.file || item?.originFileObj;
      
      if (!(actualFile instanceof File)) {
        console.error('Некорректный элемент reviewFiles:', item);
        throw new Error('Не удалось извлечь файл для загрузки');
      }

      const formData = new FormData();
      // ВАЖНО: пробуем оба варианта - 'files' и 'file'
      formData.append('files', actualFile);

      try {
        await apiClient.post(
          `/order-review-media/reviews/${reviewId}/attachments?sortOrder=${i}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } catch (error) {
        console.error(`Ошибка загрузки файла ${actualFile.name}:`, error.response?.data || error);
        throw error;
      }
    }
  };

  const submitReview = async () => {
    try {
      setLoadingReview(true);

      const ids = getIdsFromChat(selectedChat, userRole, executorProfile, currentUserId);
      const currentOrderId = propOrderId || getOrderIdFromChat(selectedChat);

      if (!currentOrderId) {
        alert('ID заказа не найден');
        return;
      }

      let reviewId;

      if (userRole === 'CUSTOMER') {
        if (!ids.executorId) {
          alert('ID исполнителя не найден');
          return;
        }

        const requestData = {
          text: reviewText || '',
          scoreQuality: scoreQuality || 1,
          scoreDeadlines: scoreDeadlines || 1,
          scoreBudget: scoreBudget || 1,
          scoreCommunication: scoreCommunication || 1,
          scoreOverallImpression: scoreOverallImpression || 1,
          executorId: ids.executorId,
          customerUserId: ids.customerUserId || null,
        };

        const res = await apiClient.post(
          `/orders/customer-reviews/orders/${currentOrderId}`,
          requestData
        );
        reviewId = res.data.reviewId;
      } else {
        if (!ids.executorUserId) {
          alert('ID пользователя-исполнителя не найден');
          return;
        }

        if (!ids.customerUserId) {
          alert('ID пользователя-заказчика не найден');
          return;
        }

        const requestData = {
          text: reviewText || '',
          scoreCommunication: scoreCommunication || 1,
          scoreGoodFaith: scoreGoodFaith || 1,
          scoreOverallImpression: scoreOverallImpression || 1,
          executorId: ids.executorUserId,
          executorUserId: ids.executorUserId,
          customerUserId: ids.customerUserId,
        };

        const res = await apiClient.post(
          `/orders/executor-reviews/orders/${currentOrderId}`,
          requestData
        );
        reviewId = res.data.reviewId;
      }

      if (userRole === 'CUSTOMER' && reviewFiles.length > 0 && reviewId) {
        await uploadReviewAttachments(reviewId);
      }

      if (reviewId) {
        const submitEndpoint =
          userRole === 'CUSTOMER'
            ? `/orders/customer-reviews/reviews/${reviewId}/submit`
            : `/orders/executor-reviews/reviews/${reviewId}/submit`;

        await apiClient.post(submitEndpoint);
      }

      alert('Отзыв отправлен на модерацию');
      setModalShowReview(false);
      setReviewText('');
      setReviewFiles([]);
      setCurrentReviewId(null);

      if (userRole === 'CUSTOMER') {
        setScoreQuality(1);
        setScoreDeadlines(1);
        setScoreBudget(1);
        setScoreCommunication(1);
        setScoreOverallImpression(1);
      } else {
        setScoreGoodFaith(1);
        setScoreCommunication(1);
        setScoreOverallImpression(1);
      }
    } catch (error) {
      console.error('Ошибка отправки отзыва:', error);
      const errorMessage =
        error.response?.data?.message || error.response?.data?.error || 'Не удалось отправить отзыв';
      alert(errorMessage);
    } finally {
      setLoadingReview(false);
    }
  };

  const currentInvitation = invitations.find(
    (inv) => inv.orderId === (selectedChat?.orderId || selectedChat?.order?.orderId)
  );

  const shouldShowCustomerHeaderActions =
    userRole === 'CUSTOMER' &&
    !isExecutor &&
    !!selectedChat &&
    !customerActionButtonsHidden &&
    !collaborationState?.selectedExecutorId;

  const shouldShowDesktopHeader = userRole === 'EXECUTOR' && !isMobile;
  const shouldShowDesktopFooter = userRole === 'EXECUTOR' && !isMobile;
  const showSidebar = !isMobile || mobileScreen === 'list';
  const showMainChat = !isMobile || mobileScreen === 'chat';

  // Состояния для кнопок "Принять/Отклонить" на мобилке
  const invitationId = currentInvitation?.id || selectedChat?.invitationId;
  const hasInvitation = !!invitationId;
  const isDeclined = invitationId ? declinedInvitations.has(invitationId) : false;

  const shouldShowExecutorActions =
    isExecutor &&
    isMobile &&
    !!selectedChat &&
    hasInvitation &&
    !isDeclined &&
    !processingInvitation;


  return (
    <div className="page-layout">
        { userRole === 'EXECUTOR' && (
          <Header
            rightContent={
              <button
                className="btn_user"
                style={{ marginRight: '-10px' }}
                onClick={() => navigate('/executor_personal_account')}
              >
                <img src={icon_user} alt="Иконка пользователя" />
              </button>
            }
            menuItems={[
              { label: 'Все заказы', to: '/executor_all_orders' },
              { label: 'Мои заказы', to: '/executor_my_orders' },
              { label: 'Тарифы', to: '/pricing_plans' },
              { label: 'Чаты', to: '/chat' },
            ]}
            mobileMenuItems={[
              { label: 'Все заказы', to: '/executor_all_orders' },
              { label: 'Мои заказы', to: '/executor_my_orders' },
              { label: 'Тарифы', to: '/pricing_plans' },
            ]}
          /> 
        )}
      

      {isExecutor && !isMobile && (
        <div style={{ padding: '110px 0 0 110px', display: 'flex', flexDirection: 'row' }}>
          <img src={back} alt="" onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} />
          <h1 className="chat_zag">Чаты</h1>
        </div>
      )}

      <div
        className={`chat-container ${isMobile ? 'mobile-layout' : ''}`}
        style={isExecutor && !isMobile ? { padding: '35px 110px 150px 110px'} : {}}
      >
        {showSidebar && (
          <ChatSidebar
            isMobile={isMobile}
            chatsRef={chatsRef}
            handleChatsScroll={handleChatsScroll}
            showSidebarScrollbar={showSidebarScrollbar}
            sidebarThumbPosition={sidebarThumbPosition}
            loading={loading}
            chats={chatsWithSupport}
            userRole={userRole}
            selectedChat={selectedChat}
            selectChat={selectChat}
            suggestedLoading={suggestedLoading}
            suggestedExecutors={suggestedExecutors}
            goToExecutorProfile={goToExecutorProfile}
            invitationsLoading={invitationsLoading}
            invitations={invitations}
            goToOrderDetails={goToOrderDetails}
            processingInvitation={processingInvitation}
            acceptInvitation={acceptInvitation}
            declineInvitation={declineInvitation}
            chatSearchValue={chatSearchValue}
            setChatSearchValue={setChatSearchValue}
            handleChatSearch={handleChatSearch}
          />
        )}

        {showMainChat && (
          <div className={`main-chat ${isMobile ? 'mobile-chat-screen' : ''}`} style={isMobile?{margin:'130px 0'}:{}}>
            {selectedChat ? (
              <>
                <ChatHeader
                  isMobile={isMobile}
                  isExecutor={isExecutor}
                  userRole={userRole}
                  selectedChat={selectedChat}
                  goBackToChatList={goBackToChatList}
                  showHeaderMenu={showHeaderMenu}
                  setShowHeaderMenu={setShowHeaderMenu}
                  userInfoRef={userInfoRef}
                  showOptionsMenu={showOptionsMenu}
                  setShowOptionsMenu={setShowOptionsMenu}
                  optionsButtonRef={optionsButtonRef}
                  headerMenuRef={headerMenuRef}
                  optionsMenuRef={optionsMenuRef}
                  navigate={navigate}
                  propOrderId={propOrderId}
                  getOrderIdFromChat={() => getOrderIdFromChat(selectedChat)}
                  goToExecutorProfile={goToExecutorProfile}
                  currentInvitation={currentInvitation}
                  showActionButtons={showActionButtons}
                  declinedInvitations={declinedInvitations}
                  setSelectedExecutorForAction={setSelectedExecutorForAction}
                  setShowShareContactsModal={setShowShareContactsModal}
                  setShowDeclineConfirmModal={setShowDeclineConfirmModal}
                  shouldShowCustomerHeaderActions={shouldShowCustomerHeaderActions}
                  collaborationLoading={collaborationLoading}
                  handleCustomerAcceptClick={handleCustomerAcceptClick}
                  handleCustomerDeclineClick={handleCustomerDeclineClick}
                  onSelectSupportChat={(supportChat) => {
                    setShowOptionsMenu(false);
                    selectChat(supportChat);
                  }}
                  onPinChat={handlePinChat}
                  setShowComplaintModal={setShowComplaintModal}
                  onArchiveChat={archiveChat}
                  collaborationState={collaborationState}
                  showExecutorContacts={showExecutorContacts}
                  onCompleteOrder={handleCompleteOrder}
                  onShareContacts={handleExecutorShareContacts}
                />

                { shouldShowExecutorActions && isMobile && (
                  <div className="mobile-top-actions">
                    <button onClick={handleAccept} className="header-action-accept">
                      Принять
                    </button>
                    <button onClick={handleDecline} className="header-action-decline">
                      Отклонить
                    </button>
                  </div>
                )}

                <ChatMessages
                  messages={messages}
                  onSendMessage={handleSend}
                  messagesLoading={messagesLoading}
                  messagesRef={messagesRef}
                  renderAttachments={renderAttachments}
                  isExecutor={isExecutor}
                  showContactsMessage={showContactsMessage}
                  contactsShared={contactsShared}
                  showActionButtons={showActionButtons}
                  handleShareContacts={handleShareContacts}
                  handleDeclineContacts={handleDeclineContacts}
                  showReview={showReview}
                  userRole={userRole}
                  setModalShowReview={setModalShowReview}
                  scrollThumbPosition={scrollThumbPosition}
                  showScrollbar={showScrollbar}
                  handleTrackClick={handleTrackClick}
                  handleThumbMouseDown={handleThumbMouseDown}
                  fileInputRef={fileInputRef}
                  handleFileChange={handleFileChange}
                  messageInput={messageInput}
                  setMessageInput={setMessageInput}
                  handleSend={handleSend}
                  files={files}
                  setFiles={setFiles}
                  customerProfile={customerProfile}
                  collaborationState={collaborationState}
                  showExecutorContacts={showExecutorContacts}
                  executorContacts={executorContacts}
                  shareExecutorContacts={shareExecutorContacts}
                  sharingContacts={sharingContacts}
                  showCustomerContactsButton={showCustomerContactsButton}
                  onShareExecutorContacts={shareExecutorContacts}
                  onShareCustomerContacts={confirmShareContactsAfterSelect}
                  showClosedOrderBanner={orderClosedForExecutor}
                />
              </>
            ) : (
              <div className="no-chat">Выберите чат</div>
            )}
          </div>
        )}
      </div>

      <ChatModals
        showCustomerAcceptModal={showCustomerAcceptModal}
        setShowCustomerAcceptModal={setShowCustomerAcceptModal}
        selectedChatExecutorCard={selectedChatExecutorCard}
        collaborationLoading={collaborationLoading}
        confirmCustomerAccept={confirmCustomerAccept}
        showCustomerDeclineModal={showCustomerDeclineModal}
        setShowCustomerDeclineModal={setShowCustomerDeclineModal}
        confirmCustomerDecline={confirmCustomerDecline}
        showDeclineConfirmModal={showDeclineConfirmModal}
        setShowDeclineConfirmModal={setShowDeclineConfirmModal}
        confirmDecline={confirmDecline}
        showShareContactsAfterSelectModal={showShareContactsAfterSelectModal}
        cancelShareContactsAfterSelect={cancelShareContactsAfterSelect}
        confirmShareContactsAfterSelect={confirmShareContactsAfterSelect}
        showShareContactsModal={showShareContactsModal}
        setShowShareContactsModal={setShowShareContactsModal}
        confirmShareContacts={confirmShareContacts}
        modalShowReview={modalShowReview}
        setModalShowReview={setModalShowReview}
        reviewText={reviewText}
        reviewFiles={reviewFiles}
        setReviewText={setReviewText}
        userRole={userRole}
        setReviewFiles={setReviewFiles}
        scoreQuality={scoreQuality}
        setScoreQuality={setScoreQuality}
        scoreDeadlines={scoreDeadlines}
        setScoreDeadlines={setScoreDeadlines}
        scoreBudget={scoreBudget}
        setScoreBudget={setScoreBudget}
        scoreGoodFaith={scoreGoodFaith}
        setScoreGoodFaith={setScoreGoodFaith}
        scoreCommunication={scoreCommunication}
        setScoreCommunication={setScoreCommunication}
        scoreOverallImpression={scoreOverallImpression}
        setScoreOverallImpression={setScoreOverallImpression}
        loadingReview={loadingReview}
        submitReview={submitReview}
        currentOrderId={propOrderId || selectedChat?.orderId || selectedChat?.order?.orderId}
        showAcceptSuccessModal={showAcceptSuccessModal}
        setShowAcceptSuccessModal={setShowAcceptSuccessModal}
        showComplaintModal={showComplaintModal}
        setShowComplaintModal={setShowComplaintModal}
        complaintCategories={complaintCategories}
        complaintLoading={complaintLoading}
        complaintSubmitting={complaintSubmitting}
        onSubmitComplaint={handleSubmitComplaint}
        showCompleteOrderModal={showCompleteOrderModal}
        setShowCompleteOrderModal={setShowCompleteOrderModal}
        confirmCompleteOrder={confirmCompleteOrder}
        showCompletionNotification={showCompletionNotification}
        setShowCompletionNotification={setShowCompletionNotification}
        showExecutorCompletionNotification={showExecutorCompletionNotification}
        setShowExecutorCompletionNotification={setShowExecutorCompletionNotification}

      />

      { userRole === 'EXECUTOR' && (
        <Footer />
      )}
    </div>
  );
};

export default Chat;
